<template>
    <div class="page points-page user-inventory-page">
        <div class="page-header">
            <h1>會員背包</h1>
        </div>

        <div class="content vaporwave-theme">
            <div
                class="inventory-view-tabs"
                role="tablist"
                aria-label="查詢方式"
            >
                <button
                    type="button"
                    role="tab"
                    class="inventory-view-tab"
                    :class="{
                        'inventory-view-tab--active': listMode === 'grants'
                    }"
                    :aria-selected="listMode === 'grants'"
                    @click="setListMode('grants')"
                >
                    <GiftOutlined class="inventory-view-tab-icon" />
                    <span class="inventory-view-tab-label">發放紀錄</span>
                    <span class="inventory-view-tab-desc">
                        查看所有發放與來源
                    </span>
                </button>
                <button
                    type="button"
                    role="tab"
                    class="inventory-view-tab"
                    :class="{
                        'inventory-view-tab--active': listMode === 'member'
                    }"
                    :aria-selected="listMode === 'member'"
                    @click="setListMode('member')"
                >
                    <UserOutlined class="inventory-view-tab-icon" />
                    <span class="inventory-view-tab-label">依會員查詢</span>
                    <span class="inventory-view-tab-desc">
                        先選會員，再看背包物品
                    </span>
                </button>
            </div>
            <div class="toolbar">
                <div class="filters">
                    <a-space wrap>
                        <a-select
                            v-model:value="filters.userId"
                            :options="userOptions"
                            :loading="userLoading"
                            :placeholder="
                                listMode === 'grants'
                                    ? '篩選會員（選填）'
                                    : '搜尋並選擇會員'
                            "
                            style="width: 200px"
                            show-search
                            allow-clear
                            :filter-option="false"
                            @search="onUserSearch"
                            @change="onUserChange"
                        />
                        <a-select
                            v-model:value="filters.status"
                            placeholder="狀態"
                            allow-clear
                            style="width: 130px"
                            :options="statusOptions"
                        />
                        <a-select
                            v-model:value="filters.source_type"
                            placeholder="來源"
                            allow-clear
                            style="width: 120px"
                            :options="sourceTypeOptions"
                        />
                        <a-select
                            v-model:value="filters.rarity"
                            placeholder="稀有度"
                            allow-clear
                            style="width: 120px"
                            :options="rarityOptions"
                        />
                        <a-select
                            v-model:value="filters.item_type_id"
                            placeholder="展示分類"
                            allow-clear
                            style="width: 140px"
                            :options="itemTypeOptions"
                        />
                        <a-select
                            v-model:value="filters.inventory_item_id"
                            placeholder="物品（規格）"
                            allow-clear
                            show-search
                            style="width: 220px"
                            :options="inventoryItemOptions"
                            :loading="inventoryItemLoading"
                            :filter-option="false"
                            @search="onGrantItemSearch"
                            @dropdown-visible-change="onGrantItemDropdown"
                        />
                        <a-button
                            class="unified-search-btn"
                            :disabled="listMode === 'member' && !filters.userId"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            class="unified-reset-btn"
                            @click="handleReset"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
                <div class="toolbar-actions">
                    <a-button
                        v-if="canGrant && filters.userId"
                        type="primary"
                        class="unified-add-btn"
                        @click="openGrantModal"
                    >
                        <template #icon><GiftOutlined /></template>
                        發放物品
                    </a-button>
                </div>
            </div>

            <div v-if="activeFilterTags.length" class="active-filter-tags mb-3">
                <span class="active-filter-label">篩選：</span>
                <span
                    v-for="tag in activeFilterTags"
                    :key="tag.key"
                    class="filter-chip"
                >
                    {{ tag.label }}
                    <button
                        type="button"
                        class="filter-chip-close"
                        aria-label="清除"
                        @click="clearFilterTag(tag.key)"
                    >
                        ×
                    </button>
                </span>
            </div>

            <DataTable
                :columns="displayColumns"
                :data-source="list"
                :pagination="pagination"
                :loading="loading"
                @refresh="handleSearch"
                @change="handleTableChange"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'user_name'">
                        <div
                            class="member-cell"
                            role="button"
                            tabindex="0"
                            @click="viewMemberInventory(record)"
                            @keydown.enter="viewMemberInventory(record)"
                        >
                            <UserOutlined class="member-cell-icon" />
                            <div class="member-cell-text">
                                <span class="member-cell-name">
                                    {{ record.user_name || '（未命名）' }}
                                </span>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'cover_image_url'">
                        <img
                            v-if="
                                record.cover_image_url &&
                                !imageErrorIds[record.id]
                            "
                            :src="resolveUploadAssetUrl(record.cover_image_url)"
                            alt=""
                            class="item-thumb"
                            @error="imageErrorIds[record.id] = true"
                        />
                        <div v-else class="item-thumb item-thumb-placeholder">
                            <PictureOutlined />
                        </div>
                    </template>
                    <template v-else-if="column.key === 'name_snapshot'">
                        <span class="font-medium">
                            {{ record.name_snapshot }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'rarity'">
                        <a-tag :color="rarityTagColor(record.rarity)">
                            {{
                                record.rarity_display ??
                                rarityLabel(record.rarity)
                            }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'status'">
                        <a-badge
                            :status="statusBadge(record.status)"
                            :text="statusLabel(record.status)"
                        />
                    </template>
                    <template v-else-if="column.key === 'expires_at'">
                        <span v-if="!record.expires_at" class="text-gray-400">
                            永不到期
                        </span>
                        <span
                            v-else
                            :class="{ 'text-red-500': record.is_expiring_soon }"
                        >
                            {{ record.expires_at }}
                            <a-tag
                                v-if="record.is_expiring_soon"
                                color="red"
                                size="small"
                            >
                                即將到期
                            </a-tag>
                        </span>
                    </template>
                    <template v-else-if="column.key === 'source_type'">
                        <a-tag
                            size="small"
                            :color="sourceTypeColor(record.source_type)"
                        >
                            {{ sourceTypeLabel(record.source_type) }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'acquired_at'">
                        {{ record.acquired_at || '—' }}
                    </template>
                    <template v-else-if="column.key === 'actions'">
                        <a-space size="small" class="row-actions">
                            <a-button
                                v-if="canGrant && record.status === 'available'"
                                size="small"
                                class="btn-transfer"
                                @click="openTransferModal(record)"
                            >
                                轉讓
                            </a-button>
                            <a-button
                                v-if="canUpdate"
                                size="small"
                                class="btn-edit"
                                @click="openEditModal(record)"
                            >
                                修改
                            </a-button>
                            <a-button
                                v-if="canDelete"
                                size="small"
                                class="btn-delete"
                                @click="confirmDelete(record)"
                            >
                                刪除
                            </a-button>
                            <a-button
                                v-if="canViewLogs"
                                size="small"
                                class="btn-logs"
                                @click="openLogsModal(record)"
                            >
                                紀錄
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 發放物品 Modal -->
        <a-modal
            v-model:open="grantVisible"
            title="後台發放物品"
            :width="520"
            :confirm-loading="grantLoading"
            ok-text="確認發放"
            cancel-text="取消"
            @ok="submitGrant"
            @cancel="resetGrant"
        >
            <a-form
                ref="grantFormRef"
                :model="grantData"
                :rules="grantRules"
                layout="vertical"
            >
                <a-form-item label="物品" name="inventory_item_id">
                    <a-select
                        v-model:value="grantData.inventory_item_id"
                        show-search
                        allow-clear
                        placeholder="搜尋物品名稱"
                        :options="inventoryItemOptions"
                        :loading="inventoryItemLoading"
                        :filter-option="false"
                        @search="onGrantItemSearch"
                        @dropdown-visible-change="onGrantItemDropdown"
                    />
                </a-form-item>
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="數量" name="qty">
                            <a-input-number
                                v-model:value="grantData.qty"
                                :min="1"
                                :max="999"
                                style="width: 100%"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="有效天數" name="expires_in_days">
                            <a-input-number
                                v-model:value="grantData.expires_in_days"
                                placeholder="留空 = 依物品預設"
                                :min="1"
                                style="width: 100%"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>
                <a-form-item label="備註" name="memo">
                    <a-textarea
                        v-model:value="grantData.memo"
                        placeholder="備註（選填，最長 500 字）"
                        :rows="3"
                        :maxlength="500"
                    />
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 轉讓物品 Modal -->
        <a-modal
            v-model:open="transferVisible"
            title="轉讓物品給其他會員"
            :width="480"
            :confirm-loading="transferLoading"
            ok-text="確認轉讓"
            cancel-text="取消"
            @ok="submitTransfer"
            @cancel="transferVisible = false"
        >
            <div
                v-if="transferTarget"
                class="mb-4 p-3 bg-purple-50 rounded-lg text-sm"
            >
                <div class="font-medium text-purple-900">
                    {{ transferTarget.name_snapshot }}
                </div>
                <div class="text-purple-700 mt-1">
                    原持有會員：{{
                        transferTarget.user_name ||
                        `會員 #` + transferTarget.user_id
                    }}
                </div>
            </div>
            <a-form
                ref="transferFormRef"
                :model="transferData"
                :rules="transferRules"
                layout="vertical"
            >
                <a-form-item label="目標會員" name="to_user_id">
                    <a-select
                        v-model:value="transferData.to_user_id"
                        :options="transferUserOptions"
                        :loading="transferUserLoading"
                        placeholder="搜尋並選擇接收此物品的會員"
                        style="width: 100%"
                        show-search
                        allow-clear
                        :filter-option="false"
                        @search="onTransferUserSearch"
                    />
                </a-form-item>
                <a-form-item label="轉讓原因" name="reason">
                    <a-textarea
                        v-model:value="transferData.reason"
                        placeholder="轉讓原因（選填，例如：會員申請轉讓 / 客服調配）"
                        :rows="3"
                        :maxlength="200"
                    />
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 修改物品 Modal -->
        <a-modal
            v-model:open="editVisible"
            title="修改背包物品"
            :width="480"
            :confirm-loading="editLoading"
            ok-text="確認修改"
            cancel-text="取消"
            @ok="submitEdit"
            @cancel="editVisible = false"
        >
            <div
                v-if="editTarget"
                class="mb-4 p-3 bg-gray-50 rounded-lg text-sm"
            >
                <div class="font-medium">{{ editTarget.name_snapshot }}</div>
                <div class="text-gray-500 mt-1">
                    目前狀態：
                    <a-tag :color="statusTagColor(editTarget.status)">
                        {{ statusLabel(editTarget.status) }}
                    </a-tag>
                </div>
            </div>
            <a-form ref="editFormRef" :model="editData" layout="vertical">
                <a-form-item label="狀態" name="status">
                    <a-select
                        v-model:value="editData.status"
                        allow-clear
                        placeholder="不修改狀態"
                        :options="editableStatusOptions"
                    />
                    <div
                        v-if="editData.status === 'available'"
                        class="text-orange-500 text-xs mt-1"
                    >
                        ⚠ 僅能將「使用中」或「已使用」的物品改回「可使用」
                    </div>
                </a-form-item>
                <a-form-item label="到期時間" name="expires_at">
                    <a-date-picker
                        v-model:value="editData.expires_at"
                        format="YYYY-MM-DD HH:mm:ss"
                        value-format="YYYY-MM-DD HH:mm:ss"
                        placeholder="選擇到期時間"
                        allow-clear
                        class="w-full"
                        @change="
                            (v) => {
                                editData.expires_at = applyEndOfDayString(v);
                            }
                        "
                    />
                    <div class="text-gray-400 text-xs mt-1">
                        留空表示永不到期
                    </div>
                </a-form-item>
                <a-form-item label="備註" name="memo">
                    <a-textarea
                        v-model:value="editData.memo"
                        placeholder="備註（選填）"
                        :rows="3"
                        :maxlength="500"
                    />
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 刪除確認 -->
        <a-modal
            v-model:open="deleteVisible"
            title="刪除背包物品"
            :width="440"
            :confirm-loading="deleteLoading"
            ok-text="確認刪除"
            ok-type="danger"
            cancel-text="取消"
            @ok="handleDelete"
            @cancel="deleteVisible = false"
        >
            <div v-if="deleteTarget">
                <p>
                    確定要刪除「
                    <strong>{{ deleteTarget.name_snapshot }}</strong>
                    」嗎？
                </p>
                <a-alert
                    type="error"
                    message="此操作不可恢復，物品將從背包中永久刪除"
                    show-icon
                    class="mt-3"
                />
            </div>
        </a-modal>

        <!-- 操作紀錄 Modal -->
        <a-modal
            v-model:open="logsModalVisible"
            title="物品操作紀錄"
            :width="640"
            :footer="null"
            centered
            destroy-on-close
            @cancel="logsModalVisible = false"
        >
            <div v-if="logsTarget" class="logs-modal-header">
                <span class="logs-item-name">
                    {{ logsTarget.name_snapshot }}
                </span>
                <a-tag :color="rarityTagColor(logsTarget.rarity)">
                    {{
                        logsTarget.rarity_display ??
                        rarityLabel(logsTarget.rarity)
                    }}
                </a-tag>
            </div>
            <a-spin :spinning="logsLoading">
                <a-empty
                    v-if="!logsLoading && !logsList.length"
                    description="尚無操作紀錄"
                    class="mt-8"
                />
                <div v-else class="logs-list">
                    <div
                        v-for="log in logsList"
                        :key="log.id"
                        class="log-entry"
                    >
                        <div class="log-entry-header">
                            <span class="log-event">
                                {{ formatLogEvent(log.event) }}
                            </span>
                            <span class="log-time">
                                {{ formatDateTime(log.created_at) }}
                            </span>
                        </div>
                        <div class="log-causer">
                            <span v-if="log.causer_type || log.causer_name">
                                操作人員：{{ formatLogCauser(log) }}
                            </span>
                            <span v-else class="text-gray-400">系統自動</span>
                        </div>
                        <div
                            v-if="log.old_values || log.new_values"
                            class="log-changes"
                        >
                            <div
                                v-for="diff in buildInventoryLogDiff(
                                    log.old_values,
                                    log.new_values
                                )"
                                :key="diff.key"
                                class="log-diff-row"
                            >
                                <span class="log-diff-field">
                                    {{ diff.label }}
                                </span>
                                <span class="log-diff-sep">：</span>
                                <template v-if="diff.hasOld">
                                    <span class="log-diff-old">
                                        {{ diff.oldVal }}
                                    </span>
                                    <span class="log-diff-arrow">→</span>
                                </template>
                                <span class="log-diff-new">
                                    {{ diff.newVal }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </a-spin>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import {
    GiftOutlined,
    PictureOutlined,
    UserOutlined
} from '@ant-design/icons-vue';
import DataTable from '@/components/common/DataTable.vue';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    getUserInventory,
    getInventoryGrants,
    grantUserInventory,
    updateUserInventoryItem,
    deleteUserInventoryItem,
    getUserInventoryLogs,
    transferUserInventoryItem
} from '@/api/user-inventory';
import { getInventoryItems } from '@/api/inventory-items';
import { getInventoryItemTypes } from '@/api/inventoryItemTypes';
import { searchUsers } from '@/api/users';
import { buildInventoryLogDiff, formatLogEvent } from '@/utils/logFormatter';
import formatDateTime from '@/utils/formatDateTime';
import {
    rarityLabel,
    rarityTagColor,
    INVENTORY_RARITY_LABELS
} from '@/constants/inventory';
import { applyEndOfDayString } from '@/utils/dateTimePicker';
import { resolveUploadAssetUrl } from '@/utils/resolveUploadAssetUrl';

const route = useRoute();
const router = useRouter();
const adminStore = useAdminInfo();
const listMode = ref('grants');

const setListMode = (mode) => {
    if (listMode.value === mode) return;
    listMode.value = mode;
    onListModeChange();
};

const routeItemName = computed(() =>
    typeof route.query.item_name === 'string' ? route.query.item_name : ''
);

/** 作用中的額外篩選（物品／商品） */
const activeFilterTags = computed(() => {
    const tags = [];
    if (filters.inventory_item_id) {
        const fromOptions = inventoryItemOptions.value.find(
            (o) => o.value === filters.inventory_item_id
        )?.label;
        tags.push({
            key: 'inventory_item',
            label:
                fromOptions ||
                routeItemName.value ||
                `物品 #${filters.inventory_item_id}`
        });
    }
    if (filters.product_id) {
        tags.push({
            key: 'product',
            label: routeItemName.value || `商品 #${filters.product_id}`
        });
    }
    return tags;
});

const canGrant = computed(() => adminStore.hasPermissionCode('190602'));
const canUpdate = computed(() => adminStore.hasPermissionCode('190603'));
const canDelete = computed(() => adminStore.hasPermissionCode('190604'));
const canViewLogs = computed(() => adminStore.hasPermissionCode('190605'));

// List state
const list = ref([]);
const loading = ref(false);
const imageErrorIds = ref({});
const pagination = reactive({
    current: 1,
    pageSize: 15,
    total: 0,
    showSizeChanger: true,
    showTotal: (t) => `共 ${t} 筆`
});

// Filters
const filters = reactive({
    userId: null,
    status: null,
    source_type: null,
    rarity: null,
    item_type_id: null,
    inventory_item_id: null,
    product_id: null
});

// Member search (remote)
const userOptions = ref([]);
const userLoading = ref(false);
let userSearchTimer = null;
const USER_SEARCH_DEBOUNCE_MS = 250;

const fetchUserOptions = async (keyword) => {
    if (!keyword?.trim() || keyword.trim().length < 2) {
        userOptions.value = [];
        return;
    }
    try {
        userLoading.value = true;
        const res = await searchUsers({ name: keyword.trim(), limit: 10 });
        if (res.result) {
            const arr = Array.isArray(res.data)
                ? res.data
                : (res.data?.data ?? []);
            userOptions.value = arr.map((u) => ({
                label: `${u.name || u.user_name || `#${u.id}`}`,
                value: u.id
            }));
        }
    } catch {
        /* silent */
    } finally {
        userLoading.value = false;
    }
};

const onUserSearch = (val) => {
    clearTimeout(userSearchTimer);
    userSearchTimer = setTimeout(
        () => fetchUserOptions(val),
        USER_SEARCH_DEBOUNCE_MS
    );
};

const onUserChange = (userId) => {
    if (listMode.value === 'grants') {
        return;
    }
    if (userId) {
        pagination.current = 1;
        loadList();
    } else {
        list.value = [];
        pagination.total = 0;
    }
};

const onListModeChange = () => {
    list.value = [];
    pagination.total = 0;
    pagination.current = 1;
    if (listMode.value === 'grants') {
        loadGrantList();
    }
};

// Item type options
const itemTypeOptions = ref([]);

const loadItemTypes = async () => {
    try {
        const res = await getInventoryItemTypes();
        if (res.result && Array.isArray(res.data)) {
            itemTypeOptions.value = res.data.map((t) => ({
                value: t.id,
                label: t.display_name
            }));
        }
    } catch {
        /* non-blocking */
    }
};

const buildFilterParams = () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.source_type) params.source_type = filters.source_type;
    if (filters.rarity) params.rarity = filters.rarity;
    if (filters.item_type_id) params.item_type_id = filters.item_type_id;
    if (filters.inventory_item_id)
        params.inventory_item_id = filters.inventory_item_id;
    if (filters.product_id) params.product_id = filters.product_id;
    if (listMode.value === 'grants' && filters.userId) {
        params.user_id = filters.userId;
    }
    return params;
};

const loadGrantList = async () => {
    loading.value = true;
    try {
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
            ...buildFilterParams()
        };
        const res = await getInventoryGrants(params);
        if (res.result && res.data) {
            list.value = res.data.data ?? [];
            pagination.total = res.data.pagination?.total ?? 0;
        } else {
            list.value = [];
            pagination.total = 0;
        }
    } catch {
        message.error('載入發放紀錄失敗');
        list.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

const loadList = async () => {
    if (!filters.userId) return;
    loading.value = true;
    try {
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
            ...buildFilterParams()
        };
        const res = await getUserInventory(filters.userId, params);
        // API 回傳 { result, data: { data: [], pagination: {} } }
        if (res.result && res.data) {
            list.value = res.data.data ?? [];
            pagination.total = res.data.pagination?.total ?? 0;
        } else {
            list.value = [];
            pagination.total = 0;
        }
    } catch {
        message.error('載入背包物品失敗');
        list.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

const handleSearch = () => {
    pagination.current = 1;
    if (listMode.value === 'grants') {
        loadGrantList();
    } else {
        loadList();
    }
};

const viewMemberInventory = (record) => {
    const memberLabel = record.user_name || `會員 #${record.user_id}`;
    listMode.value = 'member';
    filters.userId = record.user_id;
    userOptions.value = [{ label: memberLabel, value: record.user_id }];
    pagination.current = 1;
    const q = { ...route.query };
    delete q.view;
    router.replace({ name: 'UserInventoryList', query: q });
    loadList();
};

const handleReset = () => {
    Object.assign(filters, {
        userId: null,
        status: null,
        source_type: null,
        rarity: null,
        item_type_id: null,
        inventory_item_id: null,
        product_id: null
    });
    userOptions.value = [];
    list.value = [];
    pagination.total = 0;
    listMode.value = 'grants';
    router.replace({ name: 'UserInventoryList' });
    loadGrantList();
};

const applyRouteQueryFilters = () => {
    // 預設發放紀錄；需依會員查詢時帶 view=member
    if (route.query.view === 'member') {
        listMode.value = 'member';
    } else {
        listMode.value = 'grants';
    }
    const q = route.query;
    if (q.item_type_id) {
        filters.item_type_id = Number(q.item_type_id);
    }
    if (q.inventory_item_id) {
        filters.inventory_item_id = Number(q.inventory_item_id);
    }
    if (q.product_id) {
        filters.product_id = Number(q.product_id);
    }
};

const clearFilterTag = (key) => {
    if (key === 'inventory_item') {
        filters.inventory_item_id = null;
    }
    if (key === 'product') {
        filters.product_id = null;
    }
    const q = { ...route.query };
    delete q.inventory_item_id;
    delete q.product_id;
    delete q.item_name;
    router.replace({ name: 'UserInventoryList', query: q });
    if (listMode.value === 'grants' || filters.userId) {
        handleSearch();
    }
};
const handleTableChange = ({ pagination: pag } = {}) => {
    if (!pag?.current) return;
    pagination.current = pag.current;
    pagination.pageSize = pag.pageSize;
    if (listMode.value === 'grants') {
        loadGrantList();
    } else {
        loadList();
    }
};

// 物品定義選項（發放彈窗用：remote search，避免一次拉 200+）
const inventoryItemOptions = ref([]);
const inventoryItemLoading = ref(false);
let _grantItemSearchTimer = null;

const loadInventoryItemsForGrant = async (keyword = '') => {
    inventoryItemLoading.value = true;
    try {
        const res = await getInventoryItems({
            keyword: String(keyword || '').trim() || undefined,
            per_page: 50
        });
        const list = res?.data?.data ?? res?.data ?? [];
        inventoryItemOptions.value = (Array.isArray(list) ? list : []).map(
            (item) => ({
                value: item.id,
                label: item.name
            })
        );
    } catch {
        inventoryItemOptions.value = [];
    } finally {
        inventoryItemLoading.value = false;
    }
};

const onGrantItemSearch = (keyword) => {
    clearTimeout(_grantItemSearchTimer);
    _grantItemSearchTimer = setTimeout(
        () => loadInventoryItemsForGrant(keyword),
        300
    );
};

const onGrantItemDropdown = (open) => {
    if (open && inventoryItemOptions.value.length === 0) {
        loadInventoryItemsForGrant();
    }
};

// Grant modal
const grantVisible = ref(false);
const grantLoading = ref(false);
const grantFormRef = ref(null);
const grantData = reactive({
    inventory_item_id: null,
    qty: 1,
    expires_in_days: null,
    memo: ''
});
const grantRules = {
    inventory_item_id: [{ required: true, message: '請選擇物品' }]
};

const openGrantModal = () => {
    Object.assign(grantData, {
        inventory_item_id: null,
        qty: 1,
        expires_in_days: null,
        memo: ''
    });
    loadInventoryItemsForGrant();
    grantVisible.value = true;
};

const resetGrant = () => {
    grantFormRef.value?.resetFields();
    grantVisible.value = false;
};

const submitGrant = async () => {
    try {
        await grantFormRef.value.validate();
    } catch {
        return;
    }
    grantLoading.value = true;
    try {
        const payload = {
            inventory_item_id: grantData.inventory_item_id,
            qty: grantData.qty
        };
        if (grantData.expires_in_days)
            payload.expires_in_days = grantData.expires_in_days;
        if (grantData.memo) payload.memo = grantData.memo;
        await grantUserInventory(filters.userId, payload);
        message.success(`已成功發放 ${grantData.qty} 件物品`);
        resetGrant();
        handleSearch();
    } catch (err) {
        if (err?.response?.status !== 422) {
            const resData = err?.response?.data;
            const firstFieldError = resData?.errors
                ? Object.values(resData.errors).flat()[0]
                : null;
            message.error(firstFieldError ?? resData?.message ?? '發放失敗');
        }
    } finally {
        grantLoading.value = false;
    }
};

// Edit modal
const editVisible = ref(false);
const editLoading = ref(false);
const editFormRef = ref(null);
const editTarget = ref(null);
const editData = reactive({ status: null, expires_at: null, memo: '' });

const openEditModal = (record) => {
    editTarget.value = record;
    Object.assign(editData, {
        status: null,
        expires_at: record.expires_at ?? null,
        memo: record.memo ?? ''
    });
    editVisible.value = true;
};

const submitEdit = async () => {
    editLoading.value = true;
    try {
        const payload = {};
        if (editData.status) payload.status = editData.status;
        if (editData.expires_at !== (editTarget.value.expires_at ?? null)) {
            payload.expires_at = editData.expires_at || null;
        }
        if (editData.memo !== (editTarget.value.memo ?? ''))
            payload.memo = editData.memo;

        await updateUserInventoryItem(editTarget.value.id, payload);
        message.success('修改成功');
        editVisible.value = false;
        handleSearch();
    } catch (err) {
        if (err?.response?.status !== 422) {
            message.error(err?.response?.data?.message ?? '修改失敗');
        }
    } finally {
        editLoading.value = false;
    }
};

// Delete
const deleteVisible = ref(false);
const deleteLoading = ref(false);
const deleteTarget = ref(null);

const confirmDelete = (record) => {
    deleteTarget.value = record;
    deleteVisible.value = true;
};
const handleDelete = async () => {
    deleteLoading.value = true;
    try {
        await deleteUserInventoryItem(deleteTarget.value.id);
        message.success('刪除成功');
        deleteVisible.value = false;
        deleteTarget.value = null;
        handleSearch();
    } catch (err) {
        if (err?.response?.status !== 422) {
            message.error(err?.response?.data?.message ?? '刪除失敗');
        }
    } finally {
        deleteLoading.value = false;
    }
};

const grantColumns = computed(() => {
    const cols = [
        { title: '會員', key: 'user_name', dataIndex: 'user_name', width: 160 },
        {
            title: '圖片',
            key: 'cover_image_url',
            dataIndex: 'cover_image_url',
            width: 70,
            align: 'center'
        },
        {
            title: '物品名稱',
            key: 'name_snapshot',
            dataIndex: 'name_snapshot',
            width: 180,
            ellipsis: true
        },
        {
            title: '稀有度',
            key: 'rarity',
            dataIndex: 'rarity',
            width: 90,
            align: 'center'
        },
        { title: '狀態', key: 'status', dataIndex: 'status', width: 110 },
        {
            title: '來源',
            key: 'source_type',
            dataIndex: 'source_type',
            width: 96,
            align: 'center'
        },
        {
            title: '取得時間',
            key: 'acquired_at',
            dataIndex: 'acquired_at',
            width: 160
        }
    ];
    return cols;
});

const displayColumns = computed(() =>
    listMode.value === 'grants' ? grantColumns.value : columns.value
);

// Columns
const columns = computed(() => {
    const cols = [
        {
            title: '圖片',
            key: 'cover_image_url',
            dataIndex: 'cover_image_url',
            width: 70,
            align: 'center'
        },
        {
            title: '物品名稱',
            key: 'name_snapshot',
            dataIndex: 'name_snapshot',
            width: 200,
            ellipsis: true
        },
        {
            title: '稀有度',
            key: 'rarity',
            dataIndex: 'rarity',
            width: 90,
            align: 'center'
        },
        { title: '狀態', key: 'status', dataIndex: 'status', width: 110 },
        {
            title: '來源',
            key: 'source_type',
            dataIndex: 'source_type',
            width: 96,
            align: 'center'
        },
        {
            title: '到期時間',
            key: 'expires_at',
            dataIndex: 'expires_at',
            width: 180
        },
        {
            title: '取得時間',
            key: 'acquired_at',
            dataIndex: 'acquired_at',
            width: 160
        }
    ];
    if (canUpdate.value || canDelete.value || canViewLogs.value) {
        cols.push({
            title: '操作',
            key: 'actions',
            width: 160,
            fixed: 'right'
        });
    }
    return cols;
});

// Labels & colors
const STATUS_MAP = {
    available: { label: '可使用', badge: 'success', tag: 'green' },
    used: { label: '使用中', badge: 'processing', tag: 'blue' },
    course_completed: { label: '已使用', badge: 'default', tag: 'default' },
    transferred: { label: '已轉讓', badge: 'default', tag: 'purple' },
    expired: { label: '過期', badge: 'warning', tag: 'orange' },
    disabled: { label: '失效', badge: 'error', tag: 'red' }
};

// Transfer modal
const transferVisible = ref(false);
const transferLoading = ref(false);
const transferFormRef = ref(null);
const transferTarget = ref(null);
const transferUserOptions = ref([]);
const transferUserLoading = ref(false);
let transferUserSearchTimer = null;

const transferData = reactive({ to_user_id: null, reason: '' });
const transferRules = {
    to_user_id: [{ required: true, message: '請選擇目標會員' }]
};

const fetchTransferUserOptions = async (keyword) => {
    if (!keyword?.trim() || keyword.trim().length < 2) {
        transferUserOptions.value = [];
        return;
    }
    try {
        transferUserLoading.value = true;
        const res = await searchUsers({ name: keyword.trim(), limit: 10 });
        if (res.result) {
            const arr = Array.isArray(res.data)
                ? res.data
                : (res.data?.data ?? []);
            transferUserOptions.value = arr
                .filter((u) => u.id !== transferTarget.value?.user_id)
                .map((u) => ({
                    label: `${u.name || u.user_name || `#${u.id}`}`,
                    value: u.id
                }));
        }
    } catch {
        /* silent */
    } finally {
        transferUserLoading.value = false;
    }
};

const onTransferUserSearch = (val) => {
    clearTimeout(transferUserSearchTimer);
    transferUserSearchTimer = setTimeout(
        () => fetchTransferUserOptions(val),
        USER_SEARCH_DEBOUNCE_MS
    );
};

const openTransferModal = (record) => {
    transferTarget.value = record;
    Object.assign(transferData, { to_user_id: null, reason: '' });
    transferUserOptions.value = [];
    transferVisible.value = true;
};

const submitTransfer = async () => {
    try {
        await transferFormRef.value.validate();
    } catch {
        return;
    }
    transferLoading.value = true;
    try {
        await transferUserInventoryItem(transferTarget.value.id, {
            to_user_id: transferData.to_user_id,
            reason: transferData.reason || undefined
        });
        message.success('物品轉讓成功');
        transferVisible.value = false;
        handleSearch();
    } catch (err) {
        if (err?.response?.status !== 422) {
            const resData = err?.response?.data;
            const firstFieldError = resData?.errors
                ? Object.values(resData.errors).flat()[0]
                : null;
            message.error(firstFieldError ?? resData?.message ?? '轉讓失敗');
        }
    } finally {
        transferLoading.value = false;
    }
};
const statusLabel = (s) => STATUS_MAP[s]?.label ?? s;
const statusBadge = (s) => STATUS_MAP[s]?.badge ?? 'default';
const statusTagColor = (s) => STATUS_MAP[s]?.tag ?? 'default';

const sourceTypeLabel = (s) =>
    ({
        order: '訂單',
        gift: '贈送',
        mission: '任務',
        event: '活動',
        admin: '後台發放'
    })[s] ?? s;
const sourceTypeColor = (s) =>
    ({
        order: 'blue',
        gift: 'pink',
        mission: 'green',
        event: 'cyan',
        admin: 'purple'
    })[s] ?? 'default';
// Option lists
const statusOptions = Object.entries(STATUS_MAP).map(([value, { label }]) => ({
    value,
    label
}));
const sourceTypeOptions = [
    { value: 'order', label: '訂單' },
    { value: 'gift', label: '贈送' },
    { value: 'mission', label: '任務' },
    { value: 'event', label: '活動' },
    { value: 'admin', label: '後台發放' }
];
const rarityOptions = Object.entries(INVENTORY_RARITY_LABELS).map(
    ([value, label]) => ({
        value,
        label
    })
);
// 可透過 API 設定的狀態（依 ITEM_STATUS_SPEC_FOR_FRONTEND.md 四）
const editableStatusOptions = [
    { value: 'available', label: '可使用' },
    { value: 'used', label: '使用中' },
    { value: 'course_completed', label: '已使用' },
    { value: 'expired', label: '過期' },
    { value: 'disabled', label: '失效' }
];

// 操作紀錄 Modal
const logsModalVisible = ref(false);
const logsLoading = ref(false);
const logsTarget = ref(null);
const logsList = ref([]);

const formatLogCauser = (log) => {
    if (log.causer_name && log.causer_name !== 'System') {
        return log.causer_name;
    }
    if (!log.causer_type) {
        return log.causer_name === 'System' ? '系統' : '系統自動';
    }
    return '—';
};

const openLogsModal = async (record) => {
    logsTarget.value = record;
    logsModalVisible.value = true;
    logsList.value = [];
    logsLoading.value = true;
    try {
        const res = await getUserInventoryLogs(record.id);
        logsList.value = res.data ?? [];
    } catch {
        message.error('載入操作紀錄失敗');
    } finally {
        logsLoading.value = false;
    }
};

onMounted(async () => {
    await Promise.all([loadItemTypes(), loadInventoryItemsForGrant()]);
    applyRouteQueryFilters();
    if (listMode.value === 'grants') {
        loadGrantList();
    }
});
</script>

<style scoped>
.inventory-view-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 20px;
    padding: 6px;
    border-radius: 14px;
    border: 1px solid rgba(42, 74, 122, 0.28);
    background: rgba(255, 255, 255, 0.75);
}

@media (max-width: 640px) {
    .inventory-view-tabs {
        grid-template-columns: 1fr;
    }
}

.inventory-view-tab {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px 16px;
    border: 2px solid transparent;
    border-radius: 10px;
    background: rgba(248, 249, 252, 0.9);
    text-align: left;
    cursor: pointer;
    transition:
        border-color 0.2s,
        background 0.2s,
        box-shadow 0.2s,
        transform 0.15s;
}

.inventory-view-tab:hover {
    border-color: rgba(42, 74, 122, 0.45);
    background: #fff;
}

.inventory-view-tab--active {
    border-color: #2a4a7a;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.14),
        rgba(255, 255, 255, 0.98)
    );
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.22);
}

.inventory-view-tab-icon {
    font-size: 20px;
    color: #9ca3af;
    margin-bottom: 2px;
}

.inventory-view-tab--active .inventory-view-tab-icon {
    color: #2a4a7a;
}

.inventory-view-tab-label {
    font-size: 15px;
    font-weight: 700;
    color: #4b5563;
    font-family: 'Courier New', monospace;
}

.inventory-view-tab--active .inventory-view-tab-label {
    color: #5a3d7a;
}

.inventory-view-tab-desc {
    font-size: 12px;
    color: #9ca3af;
    line-height: 1.4;
}

.inventory-view-tab--active .inventory-view-tab-desc {
    color: #7a6a9a;
}

.item-thumb {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 6px;
}
.item-thumb-placeholder {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 6px;
    color: #bbb;
}
.logs-modal-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.2);
}
.logs-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 4px;
}
.logs-item-name {
    font-weight: 600;
    font-family: 'Courier New', monospace;
    color: #2a4a7a;
}
.log-entry {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
    padding: 12px 14px;
}
.log-entry-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
}
.log-event {
    font-weight: 600;
    color: #8b5aad;
    font-family: 'Courier New', monospace;
    font-size: 13px;
}
.log-time {
    color: #999;
    font-size: 12px;
}
.log-causer {
    color: #666;
    font-size: 13px;
    margin-bottom: 6px;
}
.log-changes {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
}
.log-diff-row {
    display: flex;
    align-items: center;
    font-size: 13px;
    flex-wrap: wrap;
    gap: 2px;
}
.log-diff-field {
    color: #888;
    font-size: 12px;
    min-width: 60px;
}
.log-diff-sep {
    color: #aaa;
}
.log-diff-old {
    color: #f87171;
    text-decoration: line-through;
    font-family: 'Courier New', monospace;
}
.log-diff-arrow {
    color: #aaa;
    padding: 0 2px;
}
.log-diff-new {
    color: #10b981;
    font-weight: 600;
    font-family: 'Courier New', monospace;
}
.active-filter-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.active-filter-label {
    font-size: 13px;
    color: #6b7280;
}

.filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    font-size: 13px;
    color: #4c1d95;
    background: rgba(138, 43, 226, 0.08);
    border: 1px solid rgba(138, 43, 226, 0.22);
    border-radius: 6px;
}

.filter-chip-close {
    border: none;
    background: transparent;
    color: #2a4a7a;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 0 2px;
}

.member-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 6px;
    border-radius: 6px;
    cursor: pointer;
    max-width: 100%;
}

.member-cell:hover {
    background: rgba(138, 43, 226, 0.08);
}

.member-cell-icon {
    color: #2a4a7a;
    font-size: 16px;
    flex-shrink: 0;
}

.member-cell-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.member-cell-name {
    font-size: 13px;
    font-weight: 600;
    color: #2a2a3a;
    line-height: 1.3;
}

.btn-transfer {
    color: #2a4a7a;
    border-color: rgba(42, 74, 122, 0.4);
    background: rgba(42, 74, 122, 0.06);
}
.btn-transfer:hover {
    color: #1e3a5f;
    border-color: #2a4a7a;
    background: rgba(42, 74, 122, 0.1);
}
</style>
